import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, Play, Pause, Headphones, Music, Disc3, ChevronLeft, ChevronRight, Lock, ThumbsUp, ThumbsDown, Share2 } from "lucide-react";
import { useAuth, BACKEND_URL } from "../context/AuthContext";
import { usePlayer as __usePlayer } from "../context/PlayerContext";
import Header from "../components/Header";
import AuthModal from "../components/AuthModal";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import RelatedLinks from "../components/RelatedLinks";
import { MonthlyLeaderboard } from "../components/MonthlyLeaderboard";

const API = BACKEND_URL;

/* ─── helpers ─── */
const fmtDuration = (ms) => {
  if (!ms) return "";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

/* ─── TrackCard ─── */
const TrackCard = ({ track, isPlaying, onPlay, myVote, voteCounts, onVote }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const url = `${window.location.origin}/discover?track=${track.id}`;
    const text = `${track.title} — ${track.artists?.join(", ") || ""}`;

    if (navigator.share) {
      navigator.share({ title: text, url }).catch(() => {});
      return;
    }

    // Copy to clipboard with fallback
    const onCopied = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(onCopied).catch(() => {
        fallbackCopy(url);
        onCopied();
      });
    } else {
      fallbackCopy(url);
      onCopied();
    }
  };

  const fallbackCopy = (text) => {
    const input = document.createElement("textarea");
    input.value = text;
    input.style.position = "fixed";
    input.style.opacity = "0";
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
  };

  return (
  <div
    data-testid={`track-card-${track.id}`}
    className="group relative flex-shrink-0 w-[170px] sm:w-[190px] bg-transparent text-left"
  >
    {/* Cover Art */}
    <button onClick={() => onPlay(track)} className="w-full focus:outline-none">
      <div className="relative aspect-square rounded-lg overflow-hidden mb-2.5 bg-white/5">
        {track.thumb ? (
          <img
            src={track.thumb}
            alt={track.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music className="w-10 h-10 text-white/20" />
          </div>
        )}
        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
              isPlaying
                ? "bg-green-500 scale-100 opacity-100"
                : "bg-white/90 scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100"
            }`}
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 text-white" />
            ) : (
              <Play className="w-5 h-5 text-black ml-0.5" />
            )}
          </div>
        </div>
        {/* Play count badge */}
        {track.plays > 0 && (
          <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white/80 text-[10px] px-1.5 py-0.5 rounded font-medium backdrop-blur-sm">
            {track.plays}
          </span>
        )}
      </div>
    </button>
    {/* Info + Votes */}
    <p className="text-white text-sm font-medium truncate leading-tight">{track.title}</p>
    <p className="text-white/40 text-xs truncate mt-0.5">
      {track.artists?.length > 0 ? track.artists.join(", ") : track.genres?.join(", ") || ""}
    </p>
    <div className="flex items-center gap-2 mt-1.5">
      <button
        onClick={(e) => { e.stopPropagation(); onVote?.(track.id, 'up'); }}
        className={`flex items-center gap-1 text-xs transition ${myVote === 'up' ? 'text-green-400' : 'text-white/25 hover:text-green-400'}`}
        data-testid={`vote-up-${track.id}`}
      >
        <ThumbsUp className="w-3.5 h-3.5" />
        {(voteCounts?.up || 0) > 0 && <span>{voteCounts.up}</span>}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onVote?.(track.id, 'down'); }}
        className={`flex items-center gap-1 text-xs transition ${myVote === 'down' ? 'text-red-400' : 'text-white/25 hover:text-red-400'}`}
        data-testid={`vote-down-${track.id}`}
      >
        <ThumbsDown className="w-3.5 h-3.5" />
        {(voteCounts?.down || 0) > 0 && <span>{voteCounts.down}</span>}
      </button>
      <div className="flex items-center gap-2 ml-auto">
        {track.duration > 0 && (
          <span className="text-white/20 text-[10px]">{fmtDuration(track.duration)}</span>
        )}
        <div className="relative">
          <button
            onClick={handleShare}
            className={`p-1 rounded transition ${copied ? "text-green-400" : "text-white/40 hover:text-purple-400"}`}
            data-testid={`share-${track.id}`}
            title="Share"
          >
            <Share2 className="w-4 h-4" />
          </button>
          {copied && (
            <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap z-10">
              Copied!
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
  );
};

/* ─── HorizontalRow (scrollable section) ─── */
const HorizontalRow = ({ title, tracks, playingId, onPlay, myVotes, allVoteCounts, onVote }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    return () => el.removeEventListener("scroll", updateArrows);
  }, [tracks, updateArrows]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 600, behavior: "smooth" });
  };

  if (!tracks?.length) return null;

  return (
    <section className="mb-10" data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-4 px-1">
        <h2 className="text-white text-lg sm:text-xl font-bold tracking-tight">{title}</h2>
        <div className="flex gap-1">
          {canScrollLeft && (
            <button
              onClick={() => scroll(-1)}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
            >
              <ChevronLeft className="w-4 h-4 text-white" />
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll(1)}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
            >
              <ChevronRight className="w-4 h-4 text-white" />
            </button>
          )}
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {tracks.map((t) => (
          <TrackCard key={t.id} track={t} isPlaying={playingId === t.id} onPlay={onPlay} myVote={myVotes?.[t.id]} voteCounts={allVoteCounts?.[t.id]} onVote={onVote} />
        ))}
      </div>
    </section>
  );
};

/* ─── MiniPlayer (bottom bar) ─── */
const MiniPlayer = ({ track, isPlaying, onToggle, audioRef, tuning, isLive, liveNowPlaying, myVote, voteCounts, onVote }) => {
  const [volume, setVolume] = useState(0.5);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [liveSeconds, setLiveSeconds] = useState(0);

  const handleVolume = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (audioRef.current) {
      audioRef.current.volume = v;
      if (v === 0) setMuted(true);
      else if (muted) { setMuted(false); audioRef.current.muted = false; }
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  // Track audio time
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime || 0);
    const onMeta = () => {
      const d = audio.duration;
      setDuration(d && isFinite(d) ? d : 0);
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", onMeta);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", onMeta);
    };
  }, [audioRef]);

  // Live stream timer
  useEffect(() => {
    if (!isLive || !isPlaying) { setLiveSeconds(0); return; }
    const start = Date.now();
    const id = setInterval(() => setLiveSeconds(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [isLive, isPlaying]);

  const fmtTime = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  };

  const fmtLive = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!track) return null;

  return (
    <div
      data-testid="mini-player"
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#181820]/95 backdrop-blur-xl border-t border-white/10"
    >
      {/* Progress bar — top edge */}
      {!isLive && duration > 0 && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-white/5" data-testid="mini-player-progress">
          <div className="h-full bg-green-500 transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}
      <div className="max-w-5xl mx-auto flex items-center gap-4 px-4 py-2.5">
        {/* Thumb — show station art when live, track art during preview */}
        <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-white/5 relative">
          {isLive && liveNowPlaying?.art ? (
            <img src={liveNowPlaying.art} alt="" className="w-full h-full object-cover" />
          ) : track.thumb ? (
            <img src={track.thumb} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music className="w-5 h-5 text-white/30" />
            </div>
          )}
        </div>
        {/* Info */}
        <div className="flex-1 min-w-0">
          {tuning ? (
            <p className="text-green-400 text-sm font-medium animate-pulse">Tuning into live channel...</p>
          ) : isLive && liveNowPlaying ? (
            <>
              <p className="text-white text-sm font-medium truncate">{liveNowPlaying.title}</p>
              <p className="text-white/40 text-xs truncate">{liveNowPlaying.artist}</p>
            </>
          ) : (
            <>
              <p className="text-white text-sm font-medium truncate">{track.title}</p>
              <p className="text-white/40 text-xs truncate">
                {track.artists?.length > 0 ? track.artists.join(", ") : ""}
              </p>
            </>
          )}
        </div>
        {/* Thumbs Up/Down */}
        {track && !tuning && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => onVote?.(track.id, 'up')}
              className={`p-1.5 rounded-full transition ${myVote === 'up' ? 'text-green-400 bg-green-500/10' : 'text-white/25 hover:text-green-400 hover:bg-white/5'}`}
              data-testid="mini-player-vote-up"
            >
              <ThumbsUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => onVote?.(track.id, 'down')}
              className={`p-1.5 rounded-full transition ${myVote === 'down' ? 'text-red-400 bg-red-500/10' : 'text-white/25 hover:text-red-400 hover:bg-white/5'}`}
              data-testid="mini-player-vote-down"
            >
              <ThumbsDown className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* Time display */}
        {isLive && isPlaying && liveSeconds > 0 ? (
          <span className="text-green-400 text-xs font-mono tabular-nums flex-shrink-0" data-testid="mini-player-live-time">
            {fmtLive(liveSeconds)}
          </span>
        ) : !isLive && duration > 0 ? (
          <span className="text-white/40 text-xs font-mono tabular-nums flex-shrink-0" data-testid="mini-player-time">
            {fmtTime(currentTime)} / {fmtTime(duration)}
          </span>
        ) : null}
        {/* LIVE Badge — only when streaming live */}
        {isLive && (
          <div className="flex items-center gap-1.5 bg-red-500/20 border border-red-500/30 px-2.5 py-1 rounded-full flex-shrink-0">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-red-400 text-[10px] font-bold tracking-wider">LIVE</span>
          </div>
        )}
        {/* Preview Badge — during 60s preview */}
        {!isLive && (
          <div className="flex items-center gap-1.5 bg-green-500/20 border border-green-500/30 px-2.5 py-1 rounded-full flex-shrink-0">
            <span className="text-green-400 text-[10px] font-bold tracking-wider">PREVIEW</span>
          </div>
        )}
        {/* Play/Pause */}
        <button
          data-testid="mini-player-toggle"
          onClick={onToggle}
          className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0 hover:scale-105 transition-transform"
        >
          {isPlaying ? (
            <Pause className="w-4 h-4 text-black" />
          ) : (
            <Play className="w-4 h-4 text-black ml-0.5" />
          )}
        </button>
        {/* Volume */}
        <div className="hidden sm:flex items-center gap-2">
          <button onClick={toggleMute} className="text-white/40 hover:text-white/70 transition">
            {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={muted ? 0 : volume}
            onChange={handleVolume}
            className="w-20 accent-green-500 h-1"
            data-testid="mini-player-volume"
          />
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   DiscoverPage  —  Main Component
   ═══════════════════════════════════════════════════ */
const DiscoverPage = () => {
  const { user } = useAuth();
  const [authMode, setAuthMode] = useState(null);
  const [allStations, setAllStations] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeGenre, setActiveGenre] = useState(0);
  const player = __usePlayer();
  const { playingTrack, isPlaying, tuning, isLive, liveNowPlaying, audioRef, handlePlay: globalHandlePlay, togglePlay } = player;
  const [myVotes, setMyVotes] = useState({});
  const [voteCounts, setVoteCounts] = useState({});

  /* ── Fetch all data ── */
  useEffect(() => {
    const load = async () => {
      try {
        const [stRes, genRes] = await Promise.all([
          fetch(`${API}/api/discover/stations?all=true`),
          fetch(`${API}/api/discover/genres`),
        ]);
        const stData = await stRes.json();
        const genData = await genRes.json();
        setAllStations(stData.stations || []);
        const genreRenames = {
          "Slow Blends": "Blend Sessions",
          "Radio": "Live Channels",
          "Remix": "Remix Sessions",
          "Videos": "Live Content",
        };
        const hiddenGenres = [];
        const mapped = (Array.isArray(genData) ? genData : [])
          .filter((g) => !hiddenGenres.includes(g.name))
          .map((g) => ({ ...g, name: genreRenames[g.name] || g.name }));
        setGenres(mapped);
      } catch (e) {
        console.error("Failed to load discover data", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* ── Fetch votes ── */
  useEffect(() => {
    fetch(`${API}/api/discover/vote-counts`).then(r => r.ok ? r.json() : {}).then(setVoteCounts).catch(() => {});
    if (user) {
      const token = localStorage.getItem('token');
      fetch(`${API}/api/discover/votes`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : {}).then(setMyVotes).catch(() => {});
    }
  }, [user]);

  const handleVote = async (trackId, vote) => {
    if (!user) { setAuthMode('register'); return; }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/api/discover/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ track_id: String(trackId), vote }),
      });
      if (res.ok) {
        const data = await res.json();
        const tid = String(trackId);
        // Update local vote state
        if (data.status === 'removed') {
          setMyVotes(prev => { const n = { ...prev }; delete n[tid]; return n; });
          setVoteCounts(prev => {
            const c = { ...(prev[tid] || { up: 0, down: 0 }) };
            c[vote] = Math.max(0, (c[vote] || 0) - 1);
            return { ...prev, [tid]: c };
          });
        } else {
          const prevVote = myVotes[tid];
          setMyVotes(prev => ({ ...prev, [tid]: vote }));
          setVoteCounts(prev => {
            const c = { ...(prev[tid] || { up: 0, down: 0 }) };
            c[vote] = (c[vote] || 0) + 1;
            if (prevVote && prevVote !== vote) c[prevVote] = Math.max(0, (c[prevVote] || 0) - 1);
            return { ...prev, [tid]: c };
          });
        }
      }
    } catch (e) { console.error('Vote failed:', e); }
  };

  /* ── Use global player with play recording ── */
  const handlePlay = useCallback((track) => {
    // Map stream field for global player
    const mapped = { ...track, stream_url: track.stream || track.stream_url || '' };
    globalHandlePlay(mapped);
    // Record play
    fetch(`${API}/api/submissions/play`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ track_title: track.title, artist_name: track.artists?.[0] || "", track_id: String(track.id || "") }),
    }).catch(() => {});
  }, [globalHandlePlay]);

  /* ── Derived data ── */
  const filtered = allStations.filter((s) => {
    if (activeGenre && !s.genres?.some((g) => genres.find((gn) => gn.id === activeGenre && gn.name === g)))
      return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        s.title?.toLowerCase().includes(q) ||
        s.artists?.some((a) => a.toLowerCase().includes(q)) ||
        s.genres?.some((g) => g.toLowerCase().includes(q))
      );
    }
    return true;
  });

  // Categorize
  const radioStations = filtered.filter((s) => s.genres?.includes("Radio") || (!s.duration && s.stream?.includes("stream")));
  const musicTracks = filtered.filter((s) => s.duration > 0);
  const blends = musicTracks.filter((s) => s.genres?.some((g) => g.toLowerCase().includes("blend") || g.toLowerCase().includes("remix")));
  const featured = [...musicTracks].sort((a, b) => (b.plays || 0) - (a.plays || 0)).slice(0, 15);
  const newest = [...musicTracks].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 15);
  const recommended = [...musicTracks].filter((s) => !blends.includes(s)).sort(() => 0.5 - Math.random()).slice(0, 15);

  return (
    <div className="min-h-screen bg-[#0c0c14] text-white">
      <SEO title="Discover Live DJs &amp; Radio Stations" description="Discover live hip-hop, R&B, Southern Soul, Jazz, and Latin radio stations and new DJ blends on DJsandMCMedia™. Explore trending tracks, new uploads, and curated mixes from The NU Vibe Network record pool." />
      <Header />

      {/* Hero */}
      <div className="pt-24 pb-4 px-4 sm:px-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-1">
          <Disc3 className="w-7 h-7 text-green-400" />
          <h1 data-testid="discover-heading" className="text-3xl sm:text-4xl font-bold tracking-tight">
            Discover Live DJs &amp; Radio Stations
          </h1>
        </div>
        <p className="text-white/60 text-sm sm:text-base mb-3 max-w-3xl">
          Explore 135+ tracks, DJ blends, and live radio channels streaming on The NU Vibe Network. Stream independent hip-hop, R&amp;B, Southern Soul, Jazz, Latin, Rock, Afrobeats, and Reggae around the clock &mdash; with new uploads from verified DJs and MCs added daily.
        </p>
        <p className="text-white/45 text-xs sm:text-sm mb-6 max-w-3xl">
          Want live 24/7 streams? Browse the <Link to="/directory" className="text-green-400 hover:text-green-300 underline">Radio Directory</Link>. Looking for artists? Head to the <Link to="/artists" className="text-green-400 hover:text-green-300 underline">Artists page</Link>. Ready to share your sound? <Link to="/submit" className="text-green-400 hover:text-green-300 underline">Submit your music</Link> to our radio pool.
        </p>

        {/* Search + Genre Pills */}
        <div className={`flex flex-col gap-3 mb-8 ${!user ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="relative max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              data-testid="discover-search"
              type="text"
              placeholder="Search artists, channels, and sounds..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-green-500/40 transition"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="genre-all"
              onClick={() => setActiveGenre(0)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition ${
                activeGenre === 0
                  ? "bg-green-500 text-black"
                  : "bg-white/8 text-white/60 hover:bg-white/12 hover:text-white"
              }`}
            >
              All
            </button>
            {genres.map((g) => (
              <button
                key={g.id}
                data-testid={`genre-${g.id}`}
                onClick={() => setActiveGenre(activeGenre === g.id ? 0 : g.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition ${
                  activeGenre === g.id
                    ? "bg-green-500 text-black"
                    : "bg-white/8 text-white/60 hover:bg-white/12 hover:text-white"
                }`}
              >
                {g.name.replace("&amp;", "&")}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        {/* Auth Gate Overlay — shown when not logged in */}
        {!user && !loading && allStations.length > 0 && (
          <div className="absolute inset-0 z-30 flex items-start justify-center" data-testid="discover-auth-gate">
            {/* Gradient fade from transparent at top to solid at bottom */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0c0c14]/70 to-[#0c0c14]" />
            <div className="relative mt-32 sm:mt-44 flex flex-col items-center text-center px-6 max-w-lg">
              <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mb-5">
                <Lock className="w-7 h-7 text-green-400" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Discover the Sounds
              </h2>
              <p className="text-white/50 text-sm sm:text-base mb-6 leading-relaxed">
                Sign up for free to browse 135+ tracks, blends, and live radio channels. Discover new artists, explore curated sounds, and tune in instantly.
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="discover-gate-signup"
                  onClick={() => setAuthMode("register")}
                  className="bg-green-500 hover:bg-green-400 text-black px-7 py-3 rounded-full text-sm font-bold transition-all hover:scale-105"
                >
                  Sign Up Free
                </button>
                <button
                  data-testid="discover-gate-login"
                  onClick={() => setAuthMode("login")}
                  className="bg-white/10 hover:bg-white/15 text-white px-7 py-3 rounded-full text-sm font-medium transition"
                >
                  Log In
                </button>
              </div>
            </div>
          </div>
        )}

        <main className={`max-w-6xl mx-auto px-4 sm:px-6 pb-32 ${!user ? "max-h-[600px] overflow-hidden" : ""}`}
          style={!user ? { filter: "blur(6px)", pointerEvents: "none", userSelect: "none" } : undefined}
        >
          {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-3">
            <div className="w-10 h-10 border-4 border-white/10 border-t-green-500 rounded-full animate-spin" />
            <p className="text-white/30 text-sm">Loading record pool...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <Headphones className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white/40 mb-2">No tracks found</h2>
            <p className="text-white/25 text-sm">
              {search ? `Nothing matching "${search}"` : "Try a different genre"}
            </p>
          </div>
        ) : search || activeGenre ? (
          /* If searching/filtering, show flat grid */
          <section data-testid="section-results">
            <h2 className="text-white text-lg font-bold mb-4">
              Results <span className="text-white/30 font-normal text-sm">({filtered.length})</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filtered.map((t) => (
                <TrackCard
                  key={t.id}
                  track={t}
                  isPlaying={isPlaying && playingTrack?.id === t.id}
                  onPlay={handlePlay}
                  myVote={myVotes[t.id]}
                  voteCounts={voteCounts[t.id]}
                  onVote={handleVote}
                />
              ))}
            </div>
          </section>
        ) : (
          /* Default: categorized horizontal rows */
          <>
            <MonthlyLeaderboard />
            <HorizontalRow title="Featured" tracks={featured} playingId={isPlaying ? playingTrack?.id : null} onPlay={handlePlay} myVotes={myVotes} allVoteCounts={voteCounts} onVote={handleVote} />
            <HorizontalRow title="New Uploads" tracks={newest} playingId={isPlaying ? playingTrack?.id : null} onPlay={handlePlay} myVotes={myVotes} allVoteCounts={voteCounts} onVote={handleVote} />
            {blends.length > 0 && (
              <HorizontalRow title="Blend Sessions" tracks={blends} playingId={isPlaying ? playingTrack?.id : null} onPlay={handlePlay} myVotes={myVotes} allVoteCounts={voteCounts} onVote={handleVote} />
            )}
            {radioStations.length > 0 && (
              <HorizontalRow title="Live Channels" tracks={radioStations} playingId={isPlaying ? playingTrack?.id : null} onPlay={handlePlay} myVotes={myVotes} allVoteCounts={voteCounts} onVote={handleVote} />
            )}
            <HorizontalRow title="Recommended" tracks={recommended} playingId={isPlaying ? playingTrack?.id : null} onPlay={handlePlay} myVotes={myVotes} allVoteCounts={voteCounts} onVote={handleVote} />
          </>
        )}
      </main>
      </div>

      {/* Mini Player now handled by GlobalPlayer in App.js */}

      {/* Auth Modal */}
      {authMode && (
        <AuthModal isOpen={true} mode={authMode} onClose={() => setAuthMode(null)} onSwitchMode={(m) => setAuthMode(m)} defaultPlan="listener" />
      )}

      <RelatedLinks keys={["directory", "artists", "network", "submit", "news", "affiliate"]} heading="More to Explore" theme="green" />

      <Footer />
    </div>
  );
};

export default DiscoverPage;
